import {
  Activity,
  CreditCard,
  Eye,
  FileUp,
  GripVertical,
  Landmark,
  PenLine,
  Search,
  Shield,
} from 'lucide-react';

import type { StepNodeType } from '../../types';
import { SEBI_STEP_ORDER, STEP_NODE_META } from '../../types';

const STEP_ICONS: Record<StepNodeType, React.ElementType> = {
  pan_verification: CreditCard,
  aadhaar_verification: Shield,
  face_match: Eye,
  liveness: Activity,
  bank_account_verification: Landmark,
  kra_check: Search,
  document_upload: FileUp,
  esign_agreement: PenLine,
};

const STEP_COLORS: Record<StepNodeType, string> = {
  pan_verification: 'bg-blue-50 border-blue-200 text-blue-700',
  aadhaar_verification: 'bg-indigo-50 border-indigo-200 text-indigo-700',
  face_match: 'bg-violet-50 border-violet-200 text-violet-700',
  liveness: 'bg-purple-50 border-purple-200 text-purple-700',
  bank_account_verification: 'bg-green-50 border-green-200 text-green-700',
  kra_check: 'bg-amber-50 border-amber-200 text-amber-700',
  document_upload: 'bg-orange-50 border-orange-200 text-orange-700',
  esign_agreement: 'bg-rose-50 border-rose-200 text-rose-700',
};

function PaletteItem({ stepType }: { stepType: StepNodeType }) {
  const Icon = STEP_ICONS[stepType];
  const meta = STEP_NODE_META[stepType];
  const colorClass = STEP_COLORS[stepType];

  function handleDragStart(e: React.DragEvent) {
    e.dataTransfer.setData('application/workflowNodeType', stepType);
    e.dataTransfer.effectAllowed = 'move';
  }

  return (
    <button
      type="button"
      draggable
      onDragStart={handleDragStart}
      className="flex w-full items-center gap-2.5 px-3 py-2.5 rounded-lg border border-placeholder/40 bg-white hover:border-primary/40 hover:shadow-sm cursor-grab active:cursor-grabbing transition-all select-none text-start"
    >
      <GripVertical className="w-3.5 h-3.5 text-placeholder flex-shrink-0" />
      <div
        className={`flex-shrink-0 w-7 h-7 rounded-md flex items-center justify-center border ${colorClass}`}
      >
        <Icon className="w-3.5 h-3.5" />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-medium text-gray30 leading-tight truncate">{meta.label}</p>
      </div>
    </button>
  );
}

export function NodePalette() {
  return (
    <aside className="w-56 flex-shrink-0 border-e border-placeholder/40 bg-[#F8FAFF] flex flex-col overflow-hidden">
      <div className="px-3 py-3 border-b border-placeholder/40">
        <p className="text-xs font-semibold text-gray30 uppercase tracking-wide">Steps</p>
        <p className="text-[11px] text-placeholder mt-0.5 leading-tight">
          Drag a step onto the canvas
        </p>
      </div>
      <div className="flex-1 overflow-y-auto p-2.5 space-y-1.5">
        {SEBI_STEP_ORDER.map((stepType) => (
          <PaletteItem key={stepType} stepType={stepType} />
        ))}
      </div>
    </aside>
  );
}
